import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
const we_next_logo = '/assets/wenext_logo_dark.png';

export function LandingPrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src={we_next_logo}
                alt="WeNext Logo"
                className="w-[200px] min-w-[200px] cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">Privacy Policy</CardTitle>
            <p className="text-center text-muted-foreground">Effective Date: July 2026</p>
            <p className="text-center text-muted-foreground">Entity: PhotonX Technologies Pvt. Ltd.</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p>
                WeNext.ai ("we", "our", or "us") respects your privacy and is committed to protecting your data.
              </p>
              <p>
                This Privacy Policy explains how we collect, use, store, and protect data obtained through our website, platform, Meta APIs, and Google APIs (including Google Calendar, Google Meet via Calendar, YouTube, and Google Ads).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              <p><strong>From Users:</strong> Business name, email address, phone number, and login details.</p>
              <p><strong>From Meta APIs:</strong> WhatsApp Business Account IDs, Phone Number IDs, Template metadata, and Message delivery reports.</p>
              <p>
                We do not collect or store personal Facebook profile data such as friends, gender, or birthday.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. How We Use This Information</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>To enable WhatsApp Business onboarding via Meta Embedded Signup Flow.</li>
                <li>To sync templates and manage message delivery via WhatsApp Cloud API.</li>
                <li>To provide analytics, campaign tracking, and chat management.</li>
                <li>To send essential service notifications and system updates.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Data Sharing</h2>
              <p>We never sell or share user data with third parties.</p>
              <p>Platform Data from Meta is used only for legitimate business purposes within WeNext.ai.</p>
              <p>
                Service providers (e.g., AWS) may process data strictly under PhotonX's control and contractual data protection terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p>
                We retain business data only as long as the account remains active or as required by law.
              </p>
              <p>
                Users may request deletion of data by contacting <a href="mailto:support@photonxtech.com" className="text-primary hover:underline">support@photonxtech.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Security</h2>
              <p>We employ industry-standard security practices:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encrypted data transmission (TLS 1.2/1.3)</li>
                <li>Encrypted data storage (AES-256)</li>
                <li>Periodic access audits and restricted personnel access</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. User Rights</h2>
              <p>Users may request:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access to their stored data</li>
                <li>Correction or deletion of inaccurate data</li>
                <li>Withdrawal of consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Compliance</h2>
              <p>WeNext.ai complies with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Meta Platform Terms and Developer Policies</li>
                <li>WhatsApp Business Policy</li>
                <li>Google API Services User Data Policy (including Limited Use requirements)</li>
                <li>Applicable Indian Data Protection and IT Laws</li>
                <li>GDPR principles for international users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Google User Data</h2>
              <p>
                When you connect Google services to WeNext (Google Calendar / Google Meet, YouTube, or Google Ads), we access Google user data only through Google OAuth, only with your explicit authorization, and only to provide the user-facing features described below.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.1 Data Access</h3>
              <p>Depending on which Google integration you connect, WeNext may access the following raw Google user data:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Google Calendar / Google Meet:</strong> Google account email and profile identity used for the connection; calendar metadata; calendar events you authorize WeNext to create, update, or delete for appointments; Google Meet conference links generated as part of those calendar events.
                </li>
                <li>
                  <strong>YouTube:</strong> Google account email and profile identity; YouTube channel identity and metadata; video metadata; comments and community engagement data; YouTube Analytics reports for your connected channel.
                </li>
                <li>
                  <strong>Google Ads:</strong> Google account email and identity used for the connection; Google Ads account and campaign data you authorize WeNext to view or manage (including campaigns, lead forms, conversions, and reporting data).
                </li>
              </ul>
              <p className="mt-2">
                We may also process limited aggregated or anonymized operational metrics derived from the above (for example, delivery or sync status) solely to operate and improve these user-facing features. We do not collect Google data beyond what is required for the connected integration.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.2 Data Use</h3>
              <p>We use Google user data only to provide or improve the following user-facing features:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Google Calendar / Google Meet:</strong> Sync appointments to your Google Calendar; create, update, and delete booking events; generate and attach Google Meet links for scheduled meetings.
                </li>
                <li>
                  <strong>YouTube:</strong> Connect and manage your YouTube channel inside WeNext; sync videos and comments; upload or schedule videos; post, edit, or delete comment replies and community interactions on your behalf; display YouTube Analytics in the Insights dashboard.
                </li>
                <li>
                  <strong>Google Ads:</strong> Connect your Google Ads account; view and manage campaigns, lead forms, conversions, and reporting for accounts you explicitly authorize.
                </li>
              </ul>
              <p className="mt-2">
                Google user data is not used for targeted advertising, lending decisions, or any purpose unrelated to providing or improving these features.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.3 Data Transfer</h3>
              <p>
                Google user data is transmitted to Google APIs to operate the features above. We do not sell Google user data.
              </p>
              <p className="mt-2">
                We do not transfer or share raw or aggregated/anonymized Google user data with third parties except:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>To Google, as required to call Google APIs for the authorized features;</li>
                <li>To infrastructure processors acting under our instruction (for example, Amazon Web Services) for hosting, storage, and security;</li>
                <li>
                  For YouTube only: when you enable optional AI comment automation, YouTube comment text may be sent to OpenAI solely to generate a personalized reply for your channel. That transfer is used only to provide the feature you enabled and is not used to train generalized AI/ML models.
                </li>
                <li>When required by law or valid legal process.</li>
              </ul>
              <p className="mt-2">
                Google Calendar and Google Ads data are not sent to third-party AI services. We do not transfer Google user data to data brokers or advertisers.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.4 Data Protection</h3>
              <p>We protect Google user data using industry-standard security controls, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption in transit (TLS 1.2/1.3)</li>
                <li>Encryption at rest for OAuth credentials where applied (including AES-256-GCM for YouTube and Google Ads tokens)</li>
                <li>Access limited to authenticated business users within your organization</li>
                <li>Restricted personnel access and periodic access reviews</li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.5 Data Retention and Deletion</h3>
              <p>
                Google user data associated with an integration is retained only while the integration remains connected and as needed to provide the feature, or as required by law.
              </p>
              <p className="mt-2">
                You may disconnect Google integrations at any time inside WeNext (Scheduling → Integrations for Calendar/Meet; YouTube → Disconnect Channel; Google Ads → Disconnect). Disconnecting removes stored OAuth tokens and connection credentials for that integration. You may also revoke WeNext&apos;s access at{' '}
                <a href="https://myaccount.google.com/permissions" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  https://myaccount.google.com/permissions
                </a>
                .
              </p>
              <p className="mt-2">
                You may also request deletion of Google-related data by contacting{' '}
                <a href="mailto:privacy@photonxtech.com" className="text-primary hover:underline">privacy@photonxtech.com</a>
                {' '}or{' '}
                <a href="mailto:support@wenext.today" className="text-primary hover:underline">support@wenext.today</a>.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.6 Limited Use Restrictions</h3>
              <p>
                <strong>Prohibited data use:</strong> Raw and aggregated/anonymized Google user data is not used for any purpose other than to provide or improve user-facing features described in this policy. Use for targeted advertising, lending, or similar prohibited purposes is not permitted.
              </p>
              <p className="mt-2">
                <strong>Prohibited data transfer:</strong> Raw and aggregated/anonymized Google user data is not transferred or sold to third parties for any purpose other than to provide or improve those user-facing features, subject to the limited processor and legal exceptions above. Transfer or sale to data brokers and advertisers is prohibited.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.7 AI/ML Model Training Restrictions</h3>
              <p>
                WeNext does not use raw or aggregated/anonymized Google user data (including Calendar, YouTube, and Google Ads data) to develop, improve, or train AI/ML models, including generalized or foundation models.
              </p>
              <p className="mt-2">
                We do not transfer Google user data to third-party AI/ML services for the purpose of training their models. Optional YouTube AI comment automation, when enabled by you, is used only to generate personalized replies for your channel and does not use Google data for model training.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">9.8 Limited Use Compliance Statement</h3>
              <p>
                The use of raw or derived user data received from Google APIs (including Google Workspace / Calendar APIs and YouTube APIs) will adhere to the{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. WeNext Mobile Application</h2>
              <p>
                This section applies to the WeNext mobile app for Android and iOS. It is in addition to the rest of this policy, which continues to apply.
              </p>
              <p>
                The mobile app is a companion to your WeNext account, so your team can work leads and conversations away from a desk. Nothing described in this section is collected for advertising, and none of it is sold or shared with third parties for their own purposes.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">10.1 Device Permissions</h3>
              <p>
                We request a permission only when the feature that needs it is used, and we explain why at the moment we ask. Every permission can be declined; declining one disables only the feature it belongs to, and the rest of the app continues to work.
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Phone state and call log (Android):</strong> Detects when a phone call starts and ends, and reads your device&apos;s call log to determine that call&apos;s duration and outcome, so the app can offer to log a follow-up note against the matching CRM contact. Section 10.2 sets out exactly when the call log is read and what happens to it. Nothing leaves your device except what you choose to save.
                </li>
                <li>
                  <strong>Camera:</strong> Taking a photo to send in a conversation, attach to a message template, add to a product listing, or set as a delivery-partner profile photo. Only the image you choose to send is uploaded.
                </li>
                <li>
                  <strong>Photos and media:</strong> Selecting an existing image, video, audio file, or document to attach to a conversation or template. Only the file you choose to send is uploaded. We do not scan, index, or upload your gallery.
                </li>
                <li>
                  <strong>Location:</strong> Sharing your current location in a conversation when you choose to, and showing delivery partners their pickup and drop points on a map while an order is active. Location is accessed only when you share it, or while you are on an active delivery as an online delivery partner.
                </li>
                <li>
                  <strong>Notifications:</strong> Delivering new-message, assignment, and reminder alerts. No data leaves your device.
                </li>
                <li>
                  <strong>Display over other apps (Android):</strong> Shows a small status bubble to delivery partners who are online, so they can return to an active order while using other apps. The bubble reads nothing from your screen or from other apps.
                </li>
                <li>
                  <strong>Run in background / ignore battery optimisation (Android):</strong> Keeps the delivery-partner session alive while an order is in progress, so order updates are not lost when the device tries to suspend the app. No data leaves your device.
                </li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">10.2 Call Follow-Up</h3>
              <p>
                On Android, with your permission, the app observes whether the phone is ringing, on a call, or idle, and uses this to notice that a call has just ended. To record that call accurately it then reads your device&apos;s call log, which is where the operating system stores the call&apos;s true duration and outcome. This is also what allows a call to be logged when your device suspends the app while the dialer is open.
              </p>
              <p className="mt-2">
                The call log is read in two situations, and only these two. When a call has just ended, the app looks at calls from the previous two hours and uses the most recent one. When you choose to log a call manually against a specific contact, the app searches your call log for the most recent call to that contact&apos;s number. In both cases only the single matching call is used; every other entry is ignored, is not copied, and never leaves your device.
              </p>
              <p className="mt-2">
                The app next checks whether the other party&apos;s number belongs to a contact in your business&apos;s own CRM — first against a copy of your contacts stored on the device, and only if there is no local match does it ask our servers. <strong>If the number is not one of your CRM contacts, nothing is recorded and nothing is shown</strong>, and the app takes no further action.
              </p>
              <p className="mt-2">
                If the number does match one of your contacts, the app offers you a form. If you choose to save it, the following is stored against that contact in your CRM: the call&apos;s duration rounded up to the nearest minute, the time it ended, the outcome you select (for example connected, missed, or no answer), and any notes you type. If you dismiss the form, nothing is saved.
              </p>
              <p className="mt-2">
                Call log entries are read on your device only. We do not upload, copy, or retain your call log or call history. The only thing that reaches our servers is the summary described above, for a call to one of your own CRM contacts, that you chose to save. The app reads the call log; it never adds to, edits, or deletes entries in it.
              </p>
              <p className="mt-2"><strong>What we never access:</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>We do not read, send, or receive SMS messages.</li>
                <li>We do not record, listen to, or transcribe any call.</li>
                <li>We do not access your device&apos;s contacts or address book.</li>
                <li>We do not read the call log for any purpose other than the two described above, and we do not build or keep a history of your calls.</li>
                <li>We do not store or transmit any information about calls to or from anyone who is not already a contact in your own CRM.</li>
                <li>We do not use call information for advertising, profiling, or scoring, and we do not share it with third parties.</li>
              </ul>
              <p className="mt-2">
                The app does not request the Android <code>READ_SMS</code>, <code>SEND_SMS</code>, or <code>RECEIVE_SMS</code> permissions, and does not include any library that requests them.
              </p>
              <p className="mt-2">
                On iOS, the operating system does not expose call state to apps. The follow-up form appears only after you place a call from within WeNext, and only for the contact you called.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">10.3 Retention and Deletion</h3>
              <p>
                Anything you save through the mobile app — a call activity, a note, a lead stage change, an attachment — is stored in your organisation&apos;s WeNext account and follows the same retention and deletion terms set out in section 5. It is your organisation&apos;s data, and deleting it in the app deletes it from the account.
              </p>
              <p className="mt-2">
                Data that stays on the device, such as the offline contact cache and any queued actions waiting for connectivity, is removed when you sign out or uninstall the app.
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-2">10.4 Your Controls</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Every permission can be changed at any time in your device settings, and takes effect immediately.</li>
                <li>Declining the phone-state permission disables call follow-up entirely; nothing else in the app is affected.</li>
                <li>Uninstalling the app removes all data held on the device.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Updates</h2>
              <p>
                This Privacy Policy may be updated periodically to reflect new regulations or product features. Updates will be posted at this same URL.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Contact</h2>
              <p>For privacy or compliance inquiries:</p>
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p><strong>Email:</strong> <a href="mailto:privacy@photonxtech.com" className="text-primary hover:underline">privacy@photonxtech.com</a></p>
                <p><strong>Legal:</strong> <a href="mailto:legal@photonxtech.com" className="text-primary hover:underline">legal@photonxtech.com</a></p>
              </div>
              <p className="mt-4">
                <Link to="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
